// Generated by GitHub Copilot
import { useTranslation } from 'react-i18next'
import { Pencil, Trash2 } from 'lucide-react'
import type RoleDetail from '#core/entities/auth/roles/RoleDetail.ts'
import type UserCompany from '#core/entities/auth/users/UserCompany.ts'
import { PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { useUserState } from '#core/state/UserState.ts'
import { cn } from '#core/utils/cn.ts'
import { SettingsTable } from './settings-table.tsx'

interface RolesTabProps {
  roles: RoleDetail[]
  totalCount: number
  users: UserCompany[]
  onEdit: (roleId: string) => void
  onDelete: (roleId: string) => void
}

/** Distinct permission areas (services) the role grants any action on. */
function grantedAreaCount(role: RoleDetail): number {
  const services = new Set<PermissionService>()
  for (const p of role.permissions) {
    if (p.actions !== PermissionAction.NONE) services.add(p.service)
  }
  return services.size
}

/** Roles table: lists roles with description, assigned-user and permission counts, and row actions. */
export function RolesTab({ roles, totalCount, users, onEdit, onDelete }: RolesTabProps) {
  const { t } = useTranslation('settings')
  const userState = useUserState()

  // Custom roles are deletable; default roles are managed centrally (backend forbids delete).
  const canDelete = userState.hasPermission(PermissionService.PERMISSIONS, PermissionAction.DELETE)

  const userCountByRole = new Map<string, number>()
  for (const u of users) {
    if (u.role_id) userCountByRole.set(u.role_id, (userCountByRole.get(u.role_id) ?? 0) + 1)
  }

  return (
    <SettingsTable
      isEmpty={roles.length === 0}
      emptyLabel={t('usersTeams.noRoles')}
      countLabel={t('usersTeams.rolesShowing', { count: roles.length, total: totalCount })}
      headers={
        <>
          <th>{t('shared.fields.name')}</th>
          <th>{t('shared.fields.type')}</th>
          <th>{t('shared.fields.description')}</th>
          <th>{t('usersTeams.roleTable.users')}</th>
          <th>{t('usersTeams.roleTable.permissions')}</th>
          <th className="text-end">{t('shared.fields.actions')}</th>
        </>
      }
    >
      {roles.map(role => (
        <tr key={role.id}>
          <td className="fw-medium">{role.name}</td>
          <td>
            <span className={cn(
              'badge bg-opacity-10',
              role.is_default ? 'bg-secondary text-secondary' : 'bg-primary text-primary',
            )}>
              {role.is_default ? t('usersTeams.rolesModal.defaultGroup') : t('usersTeams.rolesModal.customGroup')}
            </span>
          </td>
          <td className="text-secondary text-truncate" style={{ maxWidth: '18rem' }}>
            {role.description || '—'}
          </td>
          <td className="text-secondary">{userCountByRole.get(role.id) ?? 0}</td>
          <td className="text-secondary">{grantedAreaCount(role)}</td>
          <td className="text-end">
            <button type="button" className="btn btn-sm btn-icon p-1" title={t('shared.actions.edit')} onClick={() => onEdit(role.id)}>
              <Pencil size={16} className="text-secondary" />
            </button>
            {!role.is_default && canDelete && (
              <button type="button" className="btn btn-sm btn-icon p-1 ms-1" title={t('shared.actions.delete')} onClick={() => onDelete(role.id)}>
                <Trash2 size={16} className="text-danger" />
              </button>
            )}
          </td>
        </tr>
      ))}
    </SettingsTable>
  )
}
